import { Button } from "@/components/Button";
import Portal from "@/components/Portal";
import { faCircleXmark } from "@fortawesome/pro-duotone-svg-icons";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    base: "fixed inset-0 z-[9999] flex items-center overflow-y-auto overflow-x-hidden overscroll-contain bg-primary-dark/60 transition-all ease-in-out",
    background:
      "relative z-[10000] mx-20 my-20 flex max-w-[800px] flex-col rounded-sm bg-white md:mx-auto md:my-30",
    closeButton: "absolute right-20 top-20",
    closeIcon: "h-20 w-20 text-primary-dark",
    content: "px-20 py-40 md:px-30 md:py-50",
  },
  variants: {
    rounded: {
      none: "rounded-none",
      sm: "rounded-sm",
      md: "rounded-md",
    },
    align: {
      top: "items-start",
      center: "items-center",
    },
    visibility: {
      true: "visible opacity-100",
      false: "invisible opacity-0",
    },
    width: {
      md: { background: "w-[600px]" },
      lg: { background: "w-[800px]" },
    },
  },
  defaultVariants: {
    rounded: "sm",
    align: "center",
    width: "lg",
  },
});

interface IProps {
  isOpen: boolean;
  toggleIsOpen: () => void;
  rounded?: VariantProps<typeof styles>["rounded"];
  align?: VariantProps<typeof styles>["align"];
  hideCloseButton?: boolean;
  children: ReactNode;
  testId?: string;
  width?: VariantProps<typeof styles>["width"];
}

const Modal = ({
  isOpen,
  toggleIsOpen,
  rounded,
  align,
  hideCloseButton,
  children,
  testId,
  width,
}: IProps) => {
  const { t } = useTranslation();
  const { base, background, closeButton, closeIcon, content } = styles({
    rounded,
    align,
    visibility: isOpen,
    width,
  });

  const handleBackgroundClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    toggleIsOpen();
  };

  return (
    <Portal data-testid={testId} wrapperId="react-portal-modal-container">
      <div className={base()}>
        <div className={background()}>
          {!hideCloseButton && (
            <div className={closeButton()}>
              <Button
                onClick={handleBackgroundClose}
                variant="link-light"
                iconPosition="end"
                icon={faCircleXmark}
                iconClassName={closeIcon()}
                width="unset">
                {t("components.modal.close")}
              </Button>
            </div>
          )}
          <div className={content()}>{children}</div>
        </div>
      </div>
    </Portal>
  );
};

export default Modal;
